import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaCalculator, FaCopy, FaCheck } from 'react-icons/fa';
import './FloatingCalculator.scss';

// ── Precision arithmetic ───────────────────────────────────────────────────────
function countDecimals(n: number): number {
    const s = n.toString();
    const dot = s.indexOf('.');
    return dot === -1 ? 0 : s.length - dot - 1;
}

/**
 * Performs +/- using integer-scaled arithmetic to avoid IEEE-754 drift.
 * Multiplication and division use toPrecision(12) rounding instead.
 */
function preciseArith(a: number, op: string, b: number): number {
    switch (op) {
        case '+': {
            const factor = Math.pow(10, Math.max(countDecimals(a), countDecimals(b)));
            return (Math.round(a * factor) + Math.round(b * factor)) / factor;
        }
        case '-': {
            const factor = Math.pow(10, Math.max(countDecimals(a), countDecimals(b)));
            return (Math.round(a * factor) - Math.round(b * factor)) / factor;
        }
        case '×': return parseFloat((a * b).toPrecision(12));
        case '÷': return b !== 0 ? parseFloat((a / b).toPrecision(12)) : NaN;
        default:   return b;
    }
}

function formatDisplay(n: number): string {
    if (isNaN(n))     return 'Error';
    if (!isFinite(n)) return n > 0 ? '∞' : '-∞';
    const rounded = parseFloat(n.toPrecision(12));
    const s = String(rounded);
    return s.length > 16 ? rounded.toExponential(6) : s;
}

// ── VAT rates ──────────────────────────────────────────────────────────────────
const VAT_RATES = [
    { label: '21%', value: 0.21 },
    { label: '9%',  value: 0.09 },
    { label: '0%',  value: 0.00 },
];

const CALC_WIDTH   = 300;
const HEADER_HEIGHT = 48;

function clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, v));
}

function defaultPos() {
    return {
        x: clamp(window.innerWidth  - CALC_WIDTH - 24, 0, window.innerWidth  - CALC_WIDTH),
        y: clamp(window.innerHeight - 500,              0, window.innerHeight - HEADER_HEIGHT),
    };
}

// ── Props ──────────────────────────────────────────────────────────────────────
interface FloatingCalculatorProps {
    open: boolean;
    onClose: () => void;
}

// ── Component ──────────────────────────────────────────────────────────────────
const FloatingCalculator: React.FC<FloatingCalculatorProps> = ({ open, onClose }) => {
    const { t } = useTranslation('common');

    // ── Position ───────────────────────────────────────────────────────────────
    const [pos, setPos] = useState<{ x: number; y: number }>(() => {
        try {
            const saved = localStorage.getItem('calcPosition');
            if (saved) {
                const p = JSON.parse(saved) as { x: number; y: number };
                return {
                    x: clamp(p.x, 0, window.innerWidth  - CALC_WIDTH),
                    y: clamp(p.y, 0, window.innerHeight - HEADER_HEIGHT),
                };
            }
        } catch { /* ignore */ }
        return defaultPos();
    });
    const posRef = useRef(pos);
    posRef.current = pos;

    // ── Mode ───────────────────────────────────────────────────────────────────
    const [mode, setMode] = useState<'calc' | 'vat'>('calc');

    // ── Calculator state ───────────────────────────────────────────────────────
    const [displayValue,      setDisplayValue]      = useState('0');
    const [pendingValue,      setPendingValue]       = useState<number | null>(null);
    const [pendingOp,         setPendingOp]          = useState<string | null>(null);
    const [waitingForOperand, setWaitingForOperand]  = useState(false);

    // ── VAT state ──────────────────────────────────────────────────────────────
    const [vatRate,       setVatRate]       = useState(0.21);
    const [vatNetInput,   setVatNetInput]   = useState('');
    const [vatGrossInput, setVatGrossInput] = useState('');
    const [vatAmount,     setVatAmount]     = useState('');
    const [vatSource,     setVatSource]     = useState<'net' | 'gross'>('net');

    // ── Clipboard ──────────────────────────────────────────────────────────────
    const [copiedKey,     setCopiedKey]     = useState<string | null>(null);
    const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const copyToClipboard = (value: string, key: string) => {
        if (!value || value === 'Error' || value === '∞' || value === '-∞') return;
        const confirm = () => {
            if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
            setCopiedKey(key);
            copiedTimerRef.current = setTimeout(() => setCopiedKey(null), 1500);
        };
        if (navigator.clipboard) {
            navigator.clipboard.writeText(value).then(confirm).catch(() => { /* ignore */ });
        } else {
            try {
                const el = document.createElement('textarea');
                el.value = value;
                el.style.cssText = 'position:fixed;opacity:0';
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                confirm();
            } catch { /* ignore */ }
        }
    };

    // Clean up copy timer on unmount
    useEffect(() => () => { if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current); }, []);

    // ── Escape to close ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    // ── Drag ──────────────────────────────────────────────────────────────────
    const onHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Don't start drag when clicking interactive elements inside the header
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();

        const startX   = e.clientX;
        const startY   = e.clientY;
        const startPosX = posRef.current.x;
        const startPosY = posRef.current.y;

        const onMove = (ev: MouseEvent) => {
            const newX = clamp(startPosX + ev.clientX - startX, 0, window.innerWidth  - CALC_WIDTH);
            const newY = clamp(startPosY + ev.clientY - startY, 0, window.innerHeight - HEADER_HEIGHT);
            posRef.current = { x: newX, y: newY };
            setPos({ x: newX, y: newY });
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            try { localStorage.setItem('calcPosition', JSON.stringify(posRef.current)); } catch { /* ignore */ }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // ── Calculator logic ───────────────────────────────────────────────────────
    const inputDigit = (digit: string) => {
        if (waitingForOperand) {
            setDisplayValue(digit);
            setWaitingForOperand(false);
        } else {
            setDisplayValue(prev => prev === '0' ? digit : prev.length >= 15 ? prev : prev + digit);
        }
    };

    const inputDecimal = () => {
        if (waitingForOperand) {
            setDisplayValue('0.');
            setWaitingForOperand(false);
            return;
        }
        if (!displayValue.includes('.')) {
            setDisplayValue(prev => prev + '.');
        }
    };

    const handleOperator = (op: string) => {
        const current = parseFloat(displayValue);
        if (pendingValue !== null && pendingOp && !waitingForOperand) {
            const result = preciseArith(pendingValue, pendingOp, current);
            const display = isNaN(result) ? 'Error' : formatDisplay(result);
            setDisplayValue(display);
            setPendingValue(isNaN(result) ? null : result);
        } else {
            setPendingValue(current);
        }
        setPendingOp(op);
        setWaitingForOperand(true);
    };

    const handleEquals = () => {
        if (pendingValue === null || pendingOp === null) return;
        const current = parseFloat(displayValue);
        const result  = preciseArith(pendingValue, pendingOp, current);
        setDisplayValue(isNaN(result) ? 'Error' : formatDisplay(result));
        setPendingValue(null);
        setPendingOp(null);
        setWaitingForOperand(true);
    };

    const handleClear = () => {
        setDisplayValue('0');
        setPendingValue(null);
        setPendingOp(null);
        setWaitingForOperand(false);
    };

    const handleBackspace = () => {
        if (waitingForOperand) return;
        setDisplayValue(prev => prev.length > 1 && prev !== 'Error' ? prev.slice(0, -1) : '0');
    };

    const handleToggleSign = () => {
        setDisplayValue(prev => {
            const n = parseFloat(prev);
            return isNaN(n) ? prev : formatDisplay(-n);
        });
    };

    const handlePercent = () => {
        setDisplayValue(prev => {
            const n = parseFloat(prev);
            if (isNaN(n)) return prev;
            return formatDisplay(preciseArith(n, '÷', 100));
        });
    };

    // ── VAT logic ──────────────────────────────────────────────────────────────
    const computeFromNet = (netStr: string, rate: number) => {
        const net = parseFloat(netStr);
        if (isNaN(net) || netStr === '') return { vat: '', gross: '' };
        const vat   = Math.round(net * rate * 100) / 100;
        const gross = Math.round((net + vat) * 100) / 100;
        return { vat: String(vat), gross: String(gross) };
    };

    const computeFromGross = (grossStr: string, rate: number) => {
        const gross = parseFloat(grossStr);
        if (isNaN(gross) || grossStr === '') return { vat: '', net: '' };
        const net = Math.round(gross / (1 + rate) * 100) / 100;
        const vat = Math.round((gross - net) * 100) / 100;
        return { vat: String(vat), net: String(net) };
    };

    const handleNetChange = (value: string, rate = vatRate) => {
        setVatNetInput(value);
        setVatSource('net');
        const { vat, gross } = computeFromNet(value, rate);
        setVatAmount(vat);
        setVatGrossInput(gross);
    };

    const handleGrossChange = (value: string, rate = vatRate) => {
        setVatGrossInput(value);
        setVatSource('gross');
        const { vat, net } = computeFromGross(value, rate);
        setVatAmount(vat);
        setVatNetInput(net);
    };

    const handleRateChange = (rate: number) => {
        setVatRate(rate);
        if (vatSource === 'net') handleNetChange(vatNetInput, rate);
        else                     handleGrossChange(vatGrossInput, rate);
    };

    const handleVatClear = () => {
        setVatNetInput('');
        setVatGrossInput('');
        setVatAmount('');
    };

    const handleCopyAllVat = () => {
        if (!vatNetInput && !vatGrossInput) return;
        const rate = Math.round(vatRate * 100);
        const lines = [
            `${t('calculator.vat.netAmount')}: €${vatNetInput  || '0'}`,
            `${t('calculator.vat.vatAmount')} (${rate}%): €${vatAmount    || '0'}`,
            `${t('calculator.vat.grossAmount')}: €${vatGrossInput || '0'}`,
        ];
        copyToClipboard(lines.join('\n'), 'all');
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    if (!open) return null;

    const opLabel = pendingValue !== null && pendingOp
        ? `${formatDisplay(pendingValue)} ${pendingOp}`
        : '\u00a0';

    return ReactDOM.createPortal(
        <div
            className="floating-calc"
            style={{ left: pos.x, top: pos.y }}
            role="dialog"
            aria-label={t('calculator.title')}
        >
            {/* ── Header ── */}
            <div className="floating-calc__header" onMouseDown={onHeaderMouseDown}>
                <FaCalculator className="floating-calc__header-icon" />
                <span className="floating-calc__header-title">{t('calculator.title')}</span>

                <div className="floating-calc__tabs">
                    <button
                        className={`floating-calc__tab${mode === 'calc' ? ' floating-calc__tab--active' : ''}`}
                        onClick={() => setMode('calc')}
                    >
                        {t('calculator.tabs.calc')}
                    </button>
                    <button
                        className={`floating-calc__tab${mode === 'vat' ? ' floating-calc__tab--active' : ''}`}
                        onClick={() => setMode('vat')}
                    >
                        {t('calculator.tabs.vat')}
                    </button>
                </div>

                <button className="floating-calc__close" onClick={onClose} aria-label={t('close')}>
                    <FaTimes />
                </button>
            </div>

            {/* ── Calc mode ── */}
            {mode === 'calc' && (
                <div className="floating-calc__body">
                    <div className="floating-calc__display">
                        <button
                            className={`floating-calc__display-copy${copiedKey === 'display' ? ' floating-calc__display-copy--copied' : ''}`}
                            onClick={() => copyToClipboard(displayValue, 'display')}
                            title={copiedKey === 'display' ? t('calculator.copied') : t('calculator.copy')}
                            aria-label={t('calculator.copy')}
                            disabled={displayValue === '0' || displayValue === 'Error'}
                        >
                            {copiedKey === 'display' ? <FaCheck /> : <FaCopy />}
                        </button>
                        <div className="floating-calc__display-op">{opLabel}</div>
                        <div className="floating-calc__display-value">{displayValue}</div>
                    </div>

                    <div className="floating-calc__keypad">
                        {/* Row 1 */}
                        <button className="floating-calc__key floating-calc__key--fn"  onClick={handleClear}>C</button>
                        <button className="floating-calc__key floating-calc__key--fn"  onClick={handleToggleSign}>+/−</button>
                        <button className="floating-calc__key floating-calc__key--fn"  onClick={handlePercent}>%</button>
                        <button className="floating-calc__key floating-calc__key--op"  onClick={() => handleOperator('÷')}>÷</button>
                        {/* Row 2 */}
                        <button className="floating-calc__key" onClick={() => inputDigit('7')}>7</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('8')}>8</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('9')}>9</button>
                        <button className="floating-calc__key floating-calc__key--op"  onClick={() => handleOperator('×')}>×</button>
                        {/* Row 3 */}
                        <button className="floating-calc__key" onClick={() => inputDigit('4')}>4</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('5')}>5</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('6')}>6</button>
                        <button className="floating-calc__key floating-calc__key--op"  onClick={() => handleOperator('-')}>−</button>
                        {/* Row 4 */}
                        <button className="floating-calc__key" onClick={() => inputDigit('1')}>1</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('2')}>2</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('3')}>3</button>
                        <button className="floating-calc__key floating-calc__key--op"  onClick={() => handleOperator('+')}>+</button>
                        {/* Row 5 */}
                        <button className="floating-calc__key floating-calc__key--fn"  onClick={handleBackspace}>⌫</button>
                        <button className="floating-calc__key" onClick={() => inputDigit('0')}>0</button>
                        <button className="floating-calc__key" onClick={inputDecimal}>.</button>
                        <button className="floating-calc__key floating-calc__key--eq"  onClick={handleEquals}>=</button>
                    </div>
                </div>
            )}

            {/* ── VAT mode ── */}
            {mode === 'vat' && (
                <div className="floating-calc__body floating-calc__body--vat">
                    {/* Rate selector */}
                    <div className="floating-calc__vat-rates">
                        {VAT_RATES.map(r => (
                            <button
                                key={r.value}
                                className={`floating-calc__rate-btn${vatRate === r.value ? ' floating-calc__rate-btn--active' : ''}`}
                                onClick={() => handleRateChange(r.value)}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>

                    {/* Net amount */}
                    <label className="floating-calc__vat-label">{t('calculator.vat.netAmount')}</label>
                    <div className="floating-calc__vat-input-wrapper">
                        <span className="floating-calc__vat-currency">€</span>
                        <input
                            type="number"
                            className="floating-calc__vat-input"
                            value={vatNetInput}
                            onChange={e => handleNetChange(e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                        />
                        <button
                            className={`floating-calc__copy-btn${copiedKey === 'net' ? ' floating-calc__copy-btn--copied' : ''}`}
                            onClick={() => copyToClipboard(vatNetInput, 'net')}
                            title={copiedKey === 'net' ? t('calculator.copied') : t('calculator.copy')}
                            aria-label={t('calculator.copy')}
                            tabIndex={-1}
                            disabled={!vatNetInput}
                        >
                            {copiedKey === 'net' ? <FaCheck /> : <FaCopy />}
                        </button>
                    </div>

                    {/* VAT amount (derived) */}
                    <label className="floating-calc__vat-label">
                        {t('calculator.vat.vatAmount')} ({Math.round(vatRate * 100)}%)
                    </label>
                    <div className="floating-calc__vat-input-wrapper floating-calc__vat-input-wrapper--derived">
                        <span className="floating-calc__vat-currency">€</span>
                        <input
                            type="number"
                            className="floating-calc__vat-input"
                            value={vatAmount}
                            readOnly
                            tabIndex={-1}
                            placeholder="0.00"
                        />
                        <button
                            className={`floating-calc__copy-btn${copiedKey === 'vat' ? ' floating-calc__copy-btn--copied' : ''}`}
                            onClick={() => copyToClipboard(vatAmount, 'vat')}
                            title={copiedKey === 'vat' ? t('calculator.copied') : t('calculator.copy')}
                            aria-label={t('calculator.copy')}
                            tabIndex={-1}
                            disabled={!vatAmount}
                        >
                            {copiedKey === 'vat' ? <FaCheck /> : <FaCopy />}
                        </button>
                    </div>

                    {/* Gross amount */}
                    <label className="floating-calc__vat-label">{t('calculator.vat.grossAmount')}</label>
                    <div className="floating-calc__vat-input-wrapper">
                        <span className="floating-calc__vat-currency">€</span>
                        <input
                            type="number"
                            className="floating-calc__vat-input"
                            value={vatGrossInput}
                            onChange={e => handleGrossChange(e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                        />
                        <button
                            className={`floating-calc__copy-btn${copiedKey === 'gross' ? ' floating-calc__copy-btn--copied' : ''}`}
                            onClick={() => copyToClipboard(vatGrossInput, 'gross')}
                            title={copiedKey === 'gross' ? t('calculator.copied') : t('calculator.copy')}
                            aria-label={t('calculator.copy')}
                            tabIndex={-1}
                            disabled={!vatGrossInput}
                        >
                            {copiedKey === 'gross' ? <FaCheck /> : <FaCopy />}
                        </button>
                    </div>

                    <div className="floating-calc__vat-actions">
                        <button className="floating-calc__vat-clear-btn" onClick={handleVatClear}>
                            {t('reset')}
                        </button>
                        <button
                            className={`floating-calc__vat-copy-all-btn${copiedKey === 'all' ? ' floating-calc__vat-copy-all-btn--copied' : ''}`}
                            onClick={handleCopyAllVat}
                            disabled={!vatNetInput && !vatGrossInput}
                            title={copiedKey === 'all' ? t('calculator.copied') : t('calculator.copyAll')}
                        >
                            {copiedKey === 'all' ? <FaCheck /> : <FaCopy />}
                            <span>{copiedKey === 'all' ? t('calculator.copied') : t('calculator.copyAll')}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};

export default FloatingCalculator;

